import { IThemeColors } from './IThemeColors';

export interface IThemePreset {
  id: string;
  name: string;
  colors: IThemeColors;
}

export const THEME_STORAGE_KEY = 'app-color-theme';
export const DEFAULT_THEME_ID = 'blue-grey';

export const themes: IThemePreset[] = [
  {
    id: 'blue-grey',
    name: 'Classic',
    colors: {
      bg: '#263238',
      bgElevated: '#29363C',
      bgSurface: '#434E54',
      bgInput: 'rgba(255, 255, 255, 0.08)',
      inputSelectionBg: 'rgba(144, 202, 249, 0.45)',
      inputSelectionText: '#FFFFFF',
      bgCode: '#1E1E1E',
      bgOverlay: '#616161',
      primary: '#90CAF9',
      primaryHover: '#74CEF9',
      primaryActive: '#1976D2',
      primaryLight: '#B3E5FC',
      textPrimary: '#FFFFFF',
      textSecondary: '#B6B6B6',
      textTertiary: 'rgba(255, 255, 255, 0.5)',
      textDisabled: '#777777',
      textOnPrimary: '#000000',
      textOnDark: 'rgba(0, 0, 0, 0.87)',
      success: '#4CAF50',
      successLight: '#C5E1A5',
      error: '#F44336',
      errorLight: '#FFAB91',
      warning: '#FF9800',
      warningLight: '#FFCA28',
      chartSuccess: '#66BB6A',
      chartSuccessDark: '#388E3C',
      chartNeutral: '#757575',
      bgCorrect: 'rgba(76, 175, 80, 0.15)',
      bgCorrectActive: 'rgba(76, 175, 80, 0.3)',
      border: 'rgba(255, 255, 255, 0.1)',
      borderError: '#AD7C6E',
      inputErrorBorder: '#AD7C6E',
      scrollbarTrack: '#2c2c2c',
      scrollbarThumb: '#939393',
      scrollbarThumbHover: '#b0b0b0',
      // Button accent (original blue)
      btnAccent: '#90CAF9',
      btnAccentHover: '#74CEF9',
      btnAccentBorder: 'rgba(144, 202, 249, 0.4)',
      btnAccentBorderHover: 'rgba(144, 202, 249, 0.6)',
      btnAccentBg: 'rgba(144, 202, 249, 0.15)',
      btnAccentBgHover: 'rgba(144, 202, 249, 0.25)',
      btnAccentOutlineHover: 'rgba(144, 202, 249, 0.06)',
      btnOutlineText: '#B6B6B6',
      // Logo
      logoFilter: 'none',
      // MUI palette mode
      paletteMode: 'dark',
    },
  },
  {
    id: 'deep-navy',
    name: 'Deep Navy',
    colors: {
      bg: '#0F172A',
      bgElevated: '#111C2E',
      bgSurface: '#16243A',
      bgInput: 'rgba(255, 255, 255, 0.07)',
      inputSelectionBg: 'rgba(51, 177, 255, 0.45)',
      inputSelectionText: '#E6EDF7',
      bgCode: '#111C2E',
      bgOverlay: '#1E2A3E',
      primary: '#33B1FF',
      primaryHover: '#5CC2FF',
      primaryActive: '#2196E0',
      primaryLight: '#38BDF8',
      textPrimary: '#E6EDF7',
      textSecondary: '#A8B7CC',
      textTertiary: 'rgba(230, 237, 247, 0.4)',
      textDisabled: '#7F91AA',
      textOnPrimary: '#000000',
      textOnDark: 'rgba(0, 0, 0, 0.87)',
      success: '#22C55E',
      successLight: '#86EFAC',
      error: '#EF4444',
      errorLight: '#FFB4A2',
      warning: '#F59E0B',
      warningLight: '#FCD34D',
      chartSuccess: '#22C55E',
      chartSuccessDark: '#16A34A',
      chartNeutral: '#3B4A61',
      bgCorrect: 'rgba(34, 197, 94, 0.15)',
      bgCorrectActive: 'rgba(34, 197, 94, 0.3)',
      border: '#2A3B55',
      borderError: '#EF4444',
      inputErrorBorder: '#EF4444',
      scrollbarTrack: '#111C2E',
      scrollbarThumb: '#3B4A61',
      scrollbarThumbHover: '#4D6080',
      // Button accent (teal)
      btnAccent: '#2DD4BF',
      btnAccentHover: '#45E0CD',
      btnAccentBorder: 'rgba(45, 212, 191, 0.4)',
      btnAccentBorderHover: 'rgba(45, 212, 191, 0.6)',
      btnAccentBg: 'rgba(45, 212, 191, 0.15)',
      btnAccentBgHover: 'rgba(45, 212, 191, 0.25)',
      btnAccentOutlineHover: 'rgba(45, 212, 191, 0.06)',
      btnOutlineText: '#A8B7CC',
      // Logo
      logoFilter: 'none',
      // MUI palette mode
      paletteMode: 'dark',
    },
  },
  {
    id: 'light',
    name: 'Light',
    colors: {
      // === BACKGROUNDS ===
      bg: '#E4E0EA', // main background — soft lavender
      bgElevated: '#EDEAF2', // cards, panels, sidebar
      bgSurface: '#D6D1DE', // tags, table rows, accordions
      bgInput: 'rgba(0, 0, 0, 0.05)', // input backgrounds
      inputSelectionBg: 'rgba(124, 58, 237, 0.28)', // selected text background in inputs
      inputSelectionText: '#2D2440', // selected text color in inputs
      bgCode: '#EDEAF2', // code editor — light bg matching bgElevated
      bgOverlay: '#C9C3D2', // dropdown/tooltip bg

      // === PRIMARY (ACCENT) ===
      primary: '#7C3AED', // violet
      primaryHover: '#8B5CF6',
      primaryActive: '#6D28D9',
      primaryLight: '#A78BFA',

      // === TEXT ===
      textPrimary: '#2D2440', // dark purple text
      textSecondary: '#5B5270', // muted purple captions
      textTertiary: 'rgba(45, 36, 64, 0.4)', // hints, placeholders
      textDisabled: '#9E96AC',
      textOnPrimary: '#FFFFFF', // text on accent buttons
      textOnDark: 'rgba(0, 0, 0, 0.87)',

      // === STATUS ===
      success: '#16A34A',
      successLight: '#BBF7D0',
      error: '#DC2626',
      errorLight: '#FECACA',
      warning: '#D97706',
      warningLight: '#FDE68A',

      // === CHART ===
      chartSuccess: '#16A34A',
      chartSuccessDark: '#15803D',
      chartNeutral: '#B3ADBE',

      // === SPECIAL ===
      bgCorrect: 'rgba(22, 163, 74, 0.12)',
      bgCorrectActive: 'rgba(22, 163, 74, 0.25)',

      // === BORDERS ===
      border: '#CCC6D5',
      borderError: '#DC2626',
      inputErrorBorder: '#B42318',

      // === SCROLLBAR ===
      scrollbarTrack: '#DDD8E4',
      scrollbarThumb: '#B3ADBE',
      scrollbarThumbHover: '#8E869C',

      // === BUTTON ACCENT (violet for light bg) ===
      btnAccent: '#7C3AED',
      btnAccentHover: '#6D28D9',
      btnAccentBorder: 'rgba(124, 58, 237, 0.35)',
      btnAccentBorderHover: 'rgba(124, 58, 237, 0.55)',
      btnAccentBg: 'rgba(124, 58, 237, 0.1)',
      btnAccentBgHover: 'rgba(124, 58, 237, 0.18)',
      btnAccentOutlineHover: 'rgba(124, 58, 237, 0.06)',
      btnOutlineText: '#5B5270',

      // === LOGO ===
      logoFilter: 'invert(1)', // invert white SVGs to dark

      // === MUI ===
      paletteMode: 'light',
    },
  },
  {
    id: 'barbie',
    name: 'Barbie',
    colors: {
      // === BACKGROUNDS ===
      bg: '#FDE8F0', // soft pink
      bgElevated: '#FFF0F5', // lighter pink cards/panels
      bgSurface: '#F5D0DC', // tags, table rows
      bgInput: 'rgba(0, 0, 0, 0.04)', // input backgrounds
      inputSelectionBg: 'rgba(233, 30, 140, 0.25)', // selected text background in inputs
      inputSelectionText: '#3D0A24', // selected text color in inputs
      bgCode: '#FFF0F5', // code editor — light pink
      bgOverlay: '#F0C0D0', // dropdown/tooltip bg

      // === PRIMARY (ACCENT) ===
      primary: '#E91E8C', // hot pink / Barbie pink
      primaryHover: '#FF3DA5',
      primaryActive: '#C2185B',
      primaryLight: '#F48FB1',

      // === TEXT ===
      textPrimary: '#3D0A24', // deep magenta-brown
      textSecondary: '#7A3055', // muted pink-brown
      textTertiary: 'rgba(61, 10, 36, 0.4)',
      textDisabled: '#BF8DA3',
      textOnPrimary: '#FFFFFF', // text on accent buttons
      textOnDark: 'rgba(0, 0, 0, 0.87)',

      // === STATUS ===
      success: '#16A34A',
      successLight: '#BBF7D0',
      error: '#DC2626',
      errorLight: '#FECACA',
      warning: '#D97706',
      warningLight: '#FDE68A',

      // === CHART ===
      chartSuccess: '#16A34A',
      chartSuccessDark: '#15803D',
      chartNeutral: '#D4A8BB',

      // === SPECIAL ===
      bgCorrect: 'rgba(22, 163, 74, 0.12)',
      bgCorrectActive: 'rgba(22, 163, 74, 0.25)',

      // === BORDERS ===
      border: '#F0B8CC',
      borderError: '#DC2626',
      inputErrorBorder: '#BE123C',

      // === SCROLLBAR ===
      scrollbarTrack: '#F5DAE4',
      scrollbarThumb: '#D4A0B5',
      scrollbarThumbHover: '#BF7A9A',

      // === BUTTON ACCENT (hot pink) ===
      btnAccent: '#E91E8C',
      btnAccentHover: '#C2185B',
      btnAccentBorder: 'rgba(233, 30, 140, 0.35)',
      btnAccentBorderHover: 'rgba(233, 30, 140, 0.55)',
      btnAccentBg: 'rgba(233, 30, 140, 0.1)',
      btnAccentBgHover: 'rgba(233, 30, 140, 0.18)',
      btnAccentOutlineHover: 'rgba(233, 30, 140, 0.06)',
      btnOutlineText: '#7A3055',

      // === LOGO ===
      logoFilter: 'invert(1)', // invert white SVGs to dark

      // === MUI ===
      paletteMode: 'light',
    },
  },
];

export function getActiveTheme(): IThemePreset {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      const found = themes.find((t) => t.id === stored);
      if (found) return found;
    }
    localStorage.setItem(THEME_STORAGE_KEY, DEFAULT_THEME_ID);
  } catch {}
  return themes.find((t) => t.id === DEFAULT_THEME_ID) ?? themes[0];
}

export function setActiveTheme(themeId: string): void {
  localStorage.setItem(THEME_STORAGE_KEY, themeId);
  window.location.reload();
}
